import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithMutation } from 'jotai-tanstack-query';

import { postChannelVerificationVerifyApi } from '@flipster/apis/v1/channel-verification';
import { ChannelType, type ChannelTypeValue } from '@flipster/apis/v1/channel-verification/types';
import { T } from '@flipster/i18n';
import { dialog } from '@flipster/react/design-system/dialog';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorMessage } from '@flipster/utils/error';

import { OfficialChannelDialog, UnofficialChannelDialog } from './dialog/search-result-dialog';

interface FormState {
  channel: ChannelTypeValue;
  searchValue: string;
}

export const channelVerificationPageBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);

  // Search form state
  const baseFormAtom = atom<FormState>({ channel: ChannelType.Telegram, searchValue: '' });
  const formAtom = atom(
    (get) => get(baseFormAtom),
    (get, set, update: Partial<FormState>) => {
      const prev = get(baseFormAtom);
      const next = { ...prev, ...update };
      // Reset searchValue when channel changes
      if (update.channel && update.channel !== prev.channel) next.searchValue = '';
      set(baseFormAtom, next);
    },
  );

  // Verify mutation
  const verifyMutationAtom = atomWithMutation(() => ({
    mutationKey: ['postChannelVerificationVerifyApi'],
    mutationFn: postChannelVerificationVerifyApi,
    onError(error: unknown) {
      snackbar.error({ message: getErrorMessage(error) });
    },
  }));
  const isVerifyingAtom = atom((get) => get(verifyMutationAtom).isPending);

  async function verify() {
    const { channel, searchValue } = store.get(formAtom);
    const value = searchValue.trim();

    if (!value) {
      return snackbar.error({ message: <T id='VIP.r260316.Please_enter_something_to_search' /> });
    }

    try {
      const { data } = await store.get(verifyMutationAtom).mutateAsync({ channel, value });

      if (data.result) {
        dialog.open(({ isOpen, promise, unmount }) => (
          <OfficialChannelDialog
            isOpen={isOpen}
            onClose={() => promise.resolve()}
            onExit={unmount}
            result={data}
            enteredSource={value}
          />
        ));
      } else {
        dialog.open(({ isOpen, promise, unmount }) => (
          <UnofficialChannelDialog
            isOpen={isOpen}
            onClose={() => promise.resolve()}
            onExit={unmount}
            result={data}
            enteredSource={value}
          />
        ));
      }
      return;
    } catch {
      // catch
    }
  }

  return {
    formAtom,
    isVerifyingAtom,
    verify,
  };
});
channelVerificationPageBunja.debugLabel = 'channelVerificationPageBunja';
